import * as React from "react";
import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AppleEmailGate() {
  const { user, userData, account, initializing, updateContactEmail } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Load dismissal state from localStorage on mount/auth state change
  useEffect(() => {
    if (user?.uid) {
      const isDismissed =
        localStorage.getItem(`apple-email-gate-dismissed-${user.uid}`) === "true" ||
        localStorage.getItem(`apple-email-gate-dont-ask-${user.uid}`) === "true";
      setDismissed(isDismissed);
    }
  }, [user?.uid]);

  // Wait until authorization details have loaded
  if (initializing || !user || !userData) {
    return null;
  }

  // Check if current user is signed in with Apple's private relay
  const isAppleAnonymous = user.email?.endsWith("@privaterelay.appleid.com");
  const hasContactEmail = !!userData.contactEmail;

  // Render nothing if they are not using Apple private relay or already provided a contact email
  if (!isAppleAnonymous || hasContactEmail) {
    return null;
  }

  const isWaiver = account?.conversionData?.utm_id === "waiver";
  const isMandatory = isWaiver;

  // Render nothing if optional and user dismissed the prompt in this session / on this device
  if (!isMandatory && dismissed) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Email address is required.");
      return;
    }
    if (!emailRegex.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      await updateContactEmail(trimmed);
    } catch (err: any) {
      setError(err?.message || "Failed to update email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (isMandatory) return;
    localStorage.setItem(`apple-email-gate-dismissed-${user.uid}`, "true");
    setDismissed(true);
  };

  return (
    <Dialog open={true} onOpenChange={() => { if (!isMandatory) handleSkip(); }}>
      <DialogContent
        showCloseButton={!isMandatory}
        onPointerDownOutside={(e) => {
          if (isMandatory) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isMandatory) e.preventDefault();
        }}
        className="max-w-md bg-white border border-slate-200 text-slate-800 rounded-3xl p-6 shadow-xl"
      >
        <DialogHeader className="text-left space-y-2">
          <DialogTitle className="text-xl font-bold text-slate-800">
            {isMandatory ? "Best Contact Email?" : "Make sure you don’t miss anything"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 leading-relaxed">
            {isMandatory
              ? "To help Dr. Clancy connect with you and help tailor your experience to your needs and goals, we need a valid email address. Please enter the best email address to reach you at."
              : (
                <>
                  Add your email to receive future updates, new feature alerts, and helpful support resources.
                  <span className="block mt-2 font-medium text-slate-600">
                    This is optional and won’t affect your access to the app.
                  </span>
                </>
              )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="contact-email" className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Contact Email Address
            </Label>
            <Input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              placeholder="you@example.com"
              disabled={loading}
              className="rounded-xl border-slate-200"
              required
            />
            {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
            {!isMandatory && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleSkip}
                disabled={loading}
                className="rounded-xl font-semibold cursor-pointer"
              >
                Maybe Later
              </Button>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="font-bold rounded-xl px-5 shadow-md transition-all cursor-pointer"
            >
              {loading ? "Updating..." : isMandatory ? "Complete Setup" : "Save Email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
